/**
 * MealPlanHistory Component
 * Displays paginated list of user's meal plans
 */

import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { mealPlanApi, MealPlan } from "../services/api";
import { MealPlanSummary } from "./MealPlanSummary";
import { ImportMealPlanModal } from "./ImportMealPlanModal";
import { MealPlanTemplateModal } from "./MealPlanTemplateModal";

interface PaginationInfo {
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
}

interface DeleteConfirmationModalProps {
    isOpen: boolean;
    mealPlan: MealPlan | null;
    onClose: () => void;
    onConfirm: () => void;
    loading?: boolean;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
    isOpen,
    mealPlan,
    onClose,
    onConfirm,
    loading = false,
}) => {
    if (!isOpen || !mealPlan) return null;

    const overlayStyle: React.CSSProperties = {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
    };

    const modalStyle: React.CSSProperties = {
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "24px",
        maxWidth: "400px",
        width: "90%",
        boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    };

    const headerStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        marginBottom: "16px",
    };

    const iconStyle: React.CSSProperties = {
        fontSize: "24px",
        color: "#dc2626",
    };

    const titleStyle: React.CSSProperties = {
        fontSize: "18px",
        fontWeight: "600",
        color: "#1f2937",
        margin: 0,
    };

    const messageStyle: React.CSSProperties = {
        color: "#4b5563",
        marginBottom: "24px",
        lineHeight: "1.5",
    };

    const mealPlanNameStyle: React.CSSProperties = {
        fontWeight: "600",
        color: "#1f2937",
    };

    const buttonGroupStyle: React.CSSProperties = {
        display: "flex",
        gap: "12px",
        justifyContent: "flex-end",
    };

    const cancelButtonStyle: React.CSSProperties = {
        padding: "10px 20px",
        border: "1px solid #d1d5db",
        borderRadius: "6px",
        backgroundColor: "white",
        color: "#374151",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "500",
        transition: "all 0.2s",
    };

    const deleteButtonStyle: React.CSSProperties = {
        padding: "10px 20px",
        border: "none",
        borderRadius: "6px",
        backgroundColor: "#dc2626",
        color: "white",
        cursor: loading ? "not-allowed" : "pointer",
        fontSize: "14px",
        fontWeight: "500",
        opacity: loading ? 0.6 : 1,
        transition: "all 0.2s",
    };

    return (
        <div style={overlayStyle} onClick={onClose}>
            <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                <div style={headerStyle}>
                    <span style={iconStyle}>⚠️</span>
                    <h3 style={titleStyle}>Delete Meal Plan</h3>
                </div>

                <div style={messageStyle}>
                    Are you sure you want to delete{" "}
                    <span style={mealPlanNameStyle}>"{mealPlan.name}"</span>?
                    <br />
                    <br />
                    This action cannot be undone.
                </div>

                <div style={buttonGroupStyle}>
                    <button
                        onClick={onClose}
                        style={cancelButtonStyle}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#f9fafb";
                            e.currentTarget.style.borderColor = "#9ca3af";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "white";
                            e.currentTarget.style.borderColor = "#d1d5db";
                        }}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        style={deleteButtonStyle}
                        onMouseEnter={(e) => {
                            if (!loading) {
                                e.currentTarget.style.backgroundColor =
                                    "#b91c1c";
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!loading) {
                                e.currentTarget.style.backgroundColor =
                                    "#dc2626";
                            }
                        }}
                        disabled={loading}
                    >
                        {loading ? "Deleting..." : "Delete"}
                    </button>
                </div>
            </div>
        </div>
    );
};

interface MealPlanHistoryProps {
    onMealPlanSelect?: (mealPlan: MealPlan) => void;
}

export const MealPlanHistory: React.FC<MealPlanHistoryProps> = ({
    onMealPlanSelect,
}) => {
    const { dbUser, profiles } = useAuth();
    const [mealPlans, setMealPlans] = useState<MealPlan[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [mealPlanToDelete, setMealPlanToDelete] = useState<MealPlan | null>(
        null
    );
    const [mealPlanForTemplate, setMealPlanForTemplate] =
        useState<MealPlan | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [pagination, setPagination] = useState<PaginationInfo>({
        currentPage: 1,
        itemsPerPage: 10,
        totalItems: 0,
        totalPages: 0,
    });

    // Show import button for testing
    const showImportButton = true;

    const loadMealPlans = useCallback(async () => {
        if (!dbUser?.id) return;

        try {
            setLoading(true);
            setError(null);

            const response = await mealPlanApi.getUserMealPlans(dbUser.id, {
                page: pagination.currentPage,
                page_size: pagination.itemsPerPage,
                sort_by: "created_at",
                sort_direction: "desc",
            });

            // If we're on a page > 1 and get no results, redirect to page 1
            if (pagination.currentPage > 1 && response.items.length === 0) {
                setPagination((prev) => ({ ...prev, currentPage: 1 }));
                return; // Return early, useEffect will trigger reload for page 1
            }

            // Additional safety check to ensure meal plans belong to current user
            const filteredMealPlans = response.items.filter(mp => {
                // This should never happen if backend is working correctly, but adds extra safety
                if (mp.user_id && mp.user_id !== dbUser.id) {
                    console.error(`Meal plan ${mp.id} belongs to user ${mp.user_id}, not current user ${dbUser.id}`);
                    return false;
                }
                return true;
            });
            
            setMealPlans(filteredMealPlans);

            // Update pagination with actual data from server
            setPagination((prev) => ({
                ...prev,
                totalItems: response.total,
                totalPages: response.pages,
            }));
        } catch (err) {
            console.error("Error loading meal plans:", err);
            setError("Failed to load meal plan history");
        } finally {
            setLoading(false);
        }
    }, [dbUser?.id, pagination.currentPage, pagination.itemsPerPage]);

    // Load meal plans when component mounts or page changes
    useEffect(() => {
        if (dbUser?.id) {
            loadMealPlans();
        }
    }, [dbUser?.id, loadMealPlans]);

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination((prev) => ({ ...prev, currentPage: newPage }));
        }
    };

    const handleMealPlanClick = (mealPlan: MealPlan) => {
        if (onMealPlanSelect) {
            onMealPlanSelect(mealPlan);
        } else {
            // TODO: Navigate to meal plan detail view or open modal
        }
    };

    const handleImportSuccess = () => {
        // Refresh the meal plans after successful import
        loadMealPlans();
    };

    const handleImportClick = () => {
        setShowImportModal(true);
    };

    const handleDeleteClick = (mealPlan: MealPlan) => {
        setMealPlanToDelete(mealPlan);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!mealPlanToDelete || !dbUser?.id) return;

        try {
            setDeleteLoading(true);
            await mealPlanApi.deleteMealPlan(dbUser.id, mealPlanToDelete.id);

            // Remove from local state
            setMealPlans((prev) =>
                prev.filter((mp) => mp.id !== mealPlanToDelete.id)
            );

            // Close modal and reset state
            setShowDeleteModal(false);
            setMealPlanToDelete(null);

            // Refresh meal plans to update pagination
            loadMealPlans();
        } catch (err) {
            console.error("Error deleting meal plan:", err);
            // TODO: Show error toast/notification
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteModal(false);
        setMealPlanToDelete(null);
    };

    const handleViewClick = (mealPlan: MealPlan) => {
        // Navigate to meal plan detail view
    };

    const handleTemplateClick = (mealPlan: MealPlan) => {
        setMealPlanForTemplate(mealPlan);
        setShowTemplateModal(true);
    };

    const handleTemplateModalClose = () => {
        setShowTemplateModal(false);
        setMealPlanForTemplate(null);
    };

    // Styles
    const containerStyle: React.CSSProperties = {
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        padding: "24px",
        minHeight: "600px",
    };

    const headerStyle: React.CSSProperties = {
        marginBottom: "24px",
        paddingBottom: "16px",
        borderBottom: "1px solid #e5e7eb",
    };

    const headerTopStyle: React.CSSProperties = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: "8px",
    };

    const titleStyle: React.CSSProperties = {
        fontSize: "24px",
        fontWeight: "600",
        color: "#1a202c",
        marginBottom: "8px",
    };

    const descriptionStyle: React.CSSProperties = {
        color: "#6b7280",
        marginBottom: "16px",
    };

    const importButtonStyle: React.CSSProperties = {
        padding: "8px 16px",
        backgroundColor: "#f59e0b",
        color: "white",
        border: "none",
        borderRadius: "6px",
        fontSize: "14px",
        fontWeight: "500",
        cursor: "pointer",
        transition: "background-color 0.2s",
        display: "flex",
        alignItems: "center",
        gap: "6px",
    };

    const listStyle: React.CSSProperties = {
        marginBottom: "32px",
    };

    const loadingStyle: React.CSSProperties = {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "200px",
        color: "#6b7280",
        fontSize: "16px",
    };

    const errorStyle: React.CSSProperties = {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "200px",
        color: "#dc2626",
        backgroundColor: "#fef2f2",
        borderRadius: "8px",
        border: "1px solid #fecaca",
        textAlign: "center",
        padding: "24px",
    };

    const emptyStyle: React.CSSProperties = {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "200px",
        color: "#6b7280",
        textAlign: "center",
        padding: "24px",
    };

    const paginationStyle: React.CSSProperties = {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",
        paddingTop: "16px",
        borderTop: "1px solid #e5e7eb",
    };

    const pageButtonStyle: React.CSSProperties = {
        padding: "8px 12px",
        border: "1px solid #d1d5db",
        borderRadius: "6px",
        backgroundColor: "white",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: 500,
        transition: "all 0.2s",
    };

    const activePageButtonStyle: React.CSSProperties = {
        ...pageButtonStyle,
        backgroundColor: "#3b82f6",
        color: "white",
        borderColor: "#3b82f6",
    };

    const disabledPageButtonStyle: React.CSSProperties = {
        ...pageButtonStyle,
        backgroundColor: "#f9fafb",
        color: "#9ca3af",
        cursor: "not-allowed",
    };

    const pageInfoStyle: React.CSSProperties = {
        color: "#6b7280",
        fontSize: "14px",
        margin: "0 16px",
    };

    // Common import button component
    const ImportButton = () =>
        showImportButton && (
            <button
                onClick={handleImportClick}
                style={importButtonStyle}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#d97706";
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#f59e0b";
                }}
            >
                📁 Import to History
            </button>
        );

    if (loading) {
        return (
            <div style={containerStyle}>
                <div style={headerStyle}>
                    <div style={headerTopStyle}>
                        <div>
                            <h1 style={titleStyle}>Meal Plan History</h1>
                        </div>
                        <ImportButton />
                    </div>
                    <p style={descriptionStyle}>
                        View and manage your previously generated meal plans.
                    </p>
                </div>
                <div style={loadingStyle}>
                    <div>Loading meal plans...</div>
                </div>
                <ImportMealPlanModal
                    isOpen={showImportModal}
                    onClose={() => setShowImportModal(false)}
                    onImportSuccess={handleImportSuccess}
                />
                <DeleteConfirmationModal
                    isOpen={showDeleteModal}
                    mealPlan={mealPlanToDelete}
                    onClose={handleDeleteCancel}
                    onConfirm={handleDeleteConfirm}
                    loading={deleteLoading}
                />
                <MealPlanTemplateModal
                    isOpen={showTemplateModal}
                    mealPlan={mealPlanForTemplate}
                    onClose={handleTemplateModalClose}
                />
            </div>
        );
    }

    if (error) {
        return (
            <div style={containerStyle}>
                <div style={headerStyle}>
                    <div style={headerTopStyle}>
                        <div>
                            <h1 style={titleStyle}>Meal Plan History</h1>
                        </div>
                        <ImportButton />
                    </div>
                    <p style={descriptionStyle}>
                        View and manage your previously generated meal plans.
                    </p>
                </div>
                <div style={errorStyle}>
                    <div style={{ fontSize: "48px", marginBottom: "16px" }}>
                        ⚠️
                    </div>
                    <div style={{ fontSize: "18px", marginBottom: "8px" }}>
                        Unable to Load History
                    </div>
                    <div>{error}</div>
                    <button
                        onClick={loadMealPlans}
                        style={{
                            marginTop: "16px",
                            padding: "8px 16px",
                            backgroundColor: "#3b82f6",
                            color: "white",
                            borderRadius: "6px",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "14px",
                        }}
                    >
                        Try Again
                    </button>
                </div>
                <ImportMealPlanModal
                    isOpen={showImportModal}
                    onClose={() => setShowImportModal(false)}
                    onImportSuccess={handleImportSuccess}
                />
                <DeleteConfirmationModal
                    isOpen={showDeleteModal}
                    mealPlan={mealPlanToDelete}
                    onClose={handleDeleteCancel}
                    onConfirm={handleDeleteConfirm}
                    loading={deleteLoading}
                />
                <MealPlanTemplateModal
                    isOpen={showTemplateModal}
                    mealPlan={mealPlanForTemplate}
                    onClose={handleTemplateModalClose}
                />
            </div>
        );
    }

    if (mealPlans.length === 0 && pagination.currentPage === 1) {
        return (
            <div style={containerStyle}>
                <div style={headerStyle}>
                    <div style={headerTopStyle}>
                        <div>
                            <h1 style={titleStyle}>Meal Plan History</h1>
                        </div>
                        <ImportButton />
                    </div>
                    <p style={descriptionStyle}>
                        View and manage your previously generated meal plans.
                    </p>
                </div>
                <div style={emptyStyle}>
                    <div style={{ fontSize: "64px", marginBottom: "16px" }}>
                        🍽️
                    </div>
                    <div style={{ fontSize: "18px", marginBottom: "8px" }}>
                        No Meal Plans Yet
                    </div>
                    <div>Create your first meal plan to see it here!</div>
                </div>
                <ImportMealPlanModal
                    isOpen={showImportModal}
                    onClose={() => setShowImportModal(false)}
                    onImportSuccess={handleImportSuccess}
                />
                <DeleteConfirmationModal
                    isOpen={showDeleteModal}
                    mealPlan={mealPlanToDelete}
                    onClose={handleDeleteCancel}
                    onConfirm={handleDeleteConfirm}
                    loading={deleteLoading}
                />
                <MealPlanTemplateModal
                    isOpen={showTemplateModal}
                    mealPlan={mealPlanForTemplate}
                    onClose={handleTemplateModalClose}
                />
            </div>
        );
    }

    return (
        <div style={containerStyle}>
            <div style={headerStyle}>
                <div style={headerTopStyle}>
                    <div>
                        <h1 style={titleStyle}>Meal Plan History</h1>
                    </div>
                    <ImportButton />
                </div>
                <p style={descriptionStyle}>
                    View and manage your previously generated meal plans.
                    {pagination.totalItems > 0 &&
                        ` Showing ${pagination.totalItems} total meal plan${
                            pagination.totalItems !== 1 ? "s" : ""
                        }.`}
                </p>
            </div>

            {/* Meal Plans List */}
            <div style={listStyle}>
                {mealPlans.map((mealPlan) => (
                    <MealPlanSummary
                        key={mealPlan.id}
                        mealPlan={mealPlan}
                        profiles={profiles}
                        variant="list"
                        onClick={() => handleMealPlanClick(mealPlan)}
                        onDelete={() => handleDeleteClick(mealPlan)}
                        onView={() => handleViewClick(mealPlan)}
                        onTemplate={() => handleTemplateClick(mealPlan)}
                    />
                ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
                <div style={paginationStyle}>
                    {/* Previous Button */}
                    <button
                        onClick={() =>
                            handlePageChange(pagination.currentPage - 1)
                        }
                        disabled={pagination.currentPage === 1}
                        style={
                            pagination.currentPage === 1
                                ? disabledPageButtonStyle
                                : pageButtonStyle
                        }
                        onMouseEnter={(e) => {
                            if (pagination.currentPage !== 1) {
                                e.currentTarget.style.borderColor = "#9ca3af";
                                e.currentTarget.style.backgroundColor =
                                    "#f9fafb";
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (pagination.currentPage !== 1) {
                                e.currentTarget.style.borderColor = "#d1d5db";
                                e.currentTarget.style.backgroundColor = "white";
                            }
                        }}
                    >
                        ← Previous
                    </button>

                    {/* Page Numbers */}
                    {Array.from(
                        { length: Math.min(5, pagination.totalPages) },
                        (_, i) => {
                            let pageNum: number;

                            if (pagination.totalPages <= 5) {
                                pageNum = i + 1;
                            } else if (pagination.currentPage <= 3) {
                                pageNum = i + 1;
                            } else if (
                                pagination.currentPage >=
                                pagination.totalPages - 2
                            ) {
                                pageNum = pagination.totalPages - 4 + i;
                            } else {
                                pageNum = pagination.currentPage - 2 + i;
                            }

                            const isActive = pageNum === pagination.currentPage;

                            return (
                                <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    style={
                                        isActive
                                            ? activePageButtonStyle
                                            : pageButtonStyle
                                    }
                                    onMouseEnter={(e) => {
                                        if (!isActive) {
                                            e.currentTarget.style.borderColor =
                                                "#9ca3af";
                                            e.currentTarget.style.backgroundColor =
                                                "#f9fafb";
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isActive) {
                                            e.currentTarget.style.borderColor =
                                                "#d1d5db";
                                            e.currentTarget.style.backgroundColor =
                                                "white";
                                        }
                                    }}
                                >
                                    {pageNum}
                                </button>
                            );
                        }
                    )}

                    {/* Page Info */}
                    <div style={pageInfoStyle}>
                        Page {pagination.currentPage} of {pagination.totalPages}
                    </div>

                    {/* Next Button */}
                    <button
                        onClick={() =>
                            handlePageChange(pagination.currentPage + 1)
                        }
                        disabled={
                            pagination.currentPage === pagination.totalPages
                        }
                        style={
                            pagination.currentPage === pagination.totalPages
                                ? disabledPageButtonStyle
                                : pageButtonStyle
                        }
                        onMouseEnter={(e) => {
                            if (
                                pagination.currentPage !== pagination.totalPages
                            ) {
                                e.currentTarget.style.borderColor = "#9ca3af";
                                e.currentTarget.style.backgroundColor =
                                    "#f9fafb";
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (
                                pagination.currentPage !== pagination.totalPages
                            ) {
                                e.currentTarget.style.borderColor = "#d1d5db";
                                e.currentTarget.style.backgroundColor = "white";
                            }
                        }}
                    >
                        Next →
                    </button>
                </div>
            )}

            {/* Import Modal */}
            <ImportMealPlanModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                onImportSuccess={handleImportSuccess}
            />

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={showDeleteModal}
                mealPlan={mealPlanToDelete}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                loading={deleteLoading}
            />

            {/* Template Modal */}
            <MealPlanTemplateModal
                isOpen={showTemplateModal}
                mealPlan={mealPlanForTemplate}
                onClose={handleTemplateModalClose}
            />
        </div>
    );
};
